import { useMemo } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { useAppDispatch, useAppSelector } from '../../redux';
import {
  linkActiveSegmentToNextSegment,
  linkActiveSegmentToPreviousSegment,
  unlinkActiveSegmentFromPreviousSegment,
  unlinkSegmentAtIndexFromNextSegment
} from '../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../redux/customRoutesApi';
import {
  getPointsToLinkSegmentToNextSegment,
  getPointsToLinkSegmentToPreviousSegment,
  isLinkedToNextSegment,
  isLinkedToPreviousSegment
} from '../../utils/customRoutes/segmentLinking';
import useRouting from './useRouting';

const useSegmentLinking = () => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const { activeSegmentIndex, isSmartRouting, segments } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    isSmartRouting: state.customRoute.isSmartRouting,
    segments: state.customRoute.route.segments
  }));

  const routing = useRouting();

  // Only peak users have access to linking segments
  const canLinkSegments = user?.featureEntitlements?.includes('build_your_own_trail');

  const linkToNextSegment = useMemo(() => {
    if (!canLinkSegments) {
      return undefined;
    }

    const points = getPointsToLinkSegmentToNextSegment(segments, activeSegmentIndex);
    if (points) {
      return async () => {
        if (isSmartRouting) {
          const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes(points) });
          const dataPoints = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;
          if (isSuccess && dataPoints) {
            dispatch(linkActiveSegmentToNextSegment({ points: dataPoints, freeDraw: false }));
          }
        } else {
          dispatch(linkActiveSegmentToNextSegment({ points, freeDraw: true }));
        }
      };
    }
    return undefined;
  }, [segments, activeSegmentIndex, isSmartRouting, dispatch, routing, canLinkSegments]);

  const linkToPreviousSegment = useMemo(() => {
    if (!canLinkSegments) {
      return undefined;
    }

    const points = getPointsToLinkSegmentToPreviousSegment(segments, activeSegmentIndex);
    if (points) {
      return async () => {
        if (isSmartRouting) {
          const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes(points) });
          const dataPoints = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;
          if (isSuccess && dataPoints) {
            dispatch(linkActiveSegmentToPreviousSegment({ points: dataPoints, freeDraw: false }));
          }
        } else {
          dispatch(linkActiveSegmentToPreviousSegment({ points, freeDraw: true }));
        }
      };
    }
    return undefined;
  }, [segments, activeSegmentIndex, isSmartRouting, routing, dispatch, canLinkSegments]);

  const unlinkFromNextSegment = useMemo(() => {
    if (!canLinkSegments) {
      return undefined;
    }

    if (isLinkedToNextSegment(segments, activeSegmentIndex)) {
      return () => {
        dispatch(unlinkSegmentAtIndexFromNextSegment(activeSegmentIndex!));
      };
    }
    return undefined;
  }, [activeSegmentIndex, dispatch, segments, canLinkSegments]);

  const unlinkFromPreviousSegment = useMemo(() => {
    if (!canLinkSegments) {
      return undefined;
    }

    if (isLinkedToPreviousSegment(segments, activeSegmentIndex)) {
      return () => {
        dispatch(unlinkActiveSegmentFromPreviousSegment());
      };
    }
    return undefined;
  }, [activeSegmentIndex, dispatch, segments, canLinkSegments]);

  return useMemo(
    () => ({
      linkToNextSegment,
      linkToPreviousSegment,
      unlinkFromNextSegment,
      unlinkFromPreviousSegment
    }),
    [linkToNextSegment, linkToPreviousSegment, unlinkFromNextSegment, unlinkFromPreviousSegment]
  );
};

export default useSegmentLinking;
